import { useRef, useEffect, useState, useCallback } from "react";
import styles from "./style.module.scss";
import classNames from "classnames";

const VideoBox = ({
  src,
  offset,
  duration,
  showVideoBox,
  handleVideoLoad,
  handleVideoError,
  handleTimeUpdate,
  videoRef: externalVideoRef,
  externalPaused = false,
}: VideoBoxProps) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const videoRef = externalVideoRef || internalVideoRef;
  const canPlay = offset >= 0;

  const assetPreviewVideoContainerClassNames = classNames(
    styles.assetPreviewVideoContainer,
    { [styles.hiddenAssetPreviewVideoContainer]: !showVideoBox },
  );

  const playVid = useCallback(async () => {
    const video = videoRef.current;
    if (video) {
      const SPECIAL_CHECK__IS_PLAYING =
        video.currentTime > 0 &&
        !video.paused &&
        !video.ended &&
        video.readyState > video.HAVE_CURRENT_DATA;
      if (!SPECIAL_CHECK__IS_PLAYING) {
        try {
          await video.play();
          setIsPlaying(true);
          handleVideoLoad();
        } catch (error) {
          handleVideoError();
          console.error("Error playing video:", error);
        }
      }
    }
    //eslint-disable-next-line
  }, []);

  const pauseVid = useCallback(() => {
    const video = videoRef.current;
    if (video && isPlaying) {
      video.pause();
      setIsPlaying(false);
    }
  }, [isPlaying, videoRef]);

  useEffect(() => {
    if (isLoaded) {
      const video = videoRef.current;
      if (!video) return;

      video.onplaying = () => setIsPlaying(true);
      video.onpause = () => setIsPlaying(false);

      if (canPlay && !externalPaused) {
        // if remaining time is lesser than duration, play only for remaining time
        const videoDuration =
          duration > video.duration - offset
            ? video.duration - offset
            : duration;

        const startTime = Math.max(0, offset);
        video.currentTime = startTime;
        playVid();
        const pauseTimeout = setTimeout(() => {
          pauseVid();
        }, videoDuration * 1000);
        return () => clearTimeout(pauseTimeout);
      }
    }
    //eslint-disable-next-line
  }, [isLoaded, isPlaying, externalPaused]);

  // Handle external pause
  useEffect(() => {
    if (externalPaused && isLoaded) {
      pauseVid();
    }
  }, [externalPaused, isLoaded, pauseVid]);

  return src ? (
    <div className={assetPreviewVideoContainerClassNames}>
      <video
        ref={videoRef}
        className={styles.assetPreviewVideo}
        autoPlay={canPlay}
        muted
        disablePictureInPicture
        playsInline
        crossOrigin="anonymous"
        onTimeUpdate={() =>
          handleTimeUpdate(videoRef.current?.currentTime || 0)
        }
        onPlay={() => setIsLoaded(true)}
        onError={() => handleVideoError()}
      >
        <source src={src} />
        Your browser does not support the video tag.
      </video>
    </div>
  ) : null;
};

export default VideoBox;

interface VideoBoxProps {
  offset: number;
  duration: number;
  src: string | null;
  showVideoBox: boolean;
  handleVideoLoad: () => void;
  handleVideoError: () => void;
  handleTimeUpdate: (currTime: number) => void;
  videoRef?: React.RefObject<HTMLVideoElement>;
  externalPaused?: boolean;
}
